/**
 * The `{placeholder}` fill of a text that a server answer or a notice names
 * (#661, bug 79). An error text, a warning text and a bout text filled the
 * placeholders with four hand-written copies of one regular expression, and
 * none of them knew that the param `role` holds a role id: the 409 of a role
 * request that the user already has read "Jūs jau turite rolę „head_coach"."
 * instead of "Vyr. treneris".
 *
 * The file has no runtime import (both imports are types and are erased), so a pure
 * spec can load it, as it loads `lib/notificationText.ts`.
 */
import type { Translations } from '../i18n';
import type { MessageParams } from './httpError';

/** A role id as a person reads it: "head_coach" gives "Head Coach" or "Vyr.
 *  treneris". An id that the dictionary does not know shows with spaces. Only an
 *  own key counts: "constructor" and "toString" are no role, and the dictionary
 *  must not answer with a function of `Object.prototype`. The `roleLabel()` of
 *  `useLabels()` and the notices read this one. */
export function roleLabelOf(t: Translations, role: string): string {
  return Object.prototype.hasOwnProperty.call(t.roles, role)
    ? (t.roles as Record<string, string>)[role]
    : role.replace(/_/g, ' ');
}

/** The gender of ONE person as a person reads it: "Male" gives "Male" or "Vyras". The
 *  server stores and sends the English word. A word that is no gender shows as it is.
 *  The `genderLabel()` of `useLabels()` and the notices read this one. */
export function genderLabelOf(t: Translations, gender: string): string {
  const g = (gender || '').toLowerCase();
  return g === 'male' ? t.common.male : g === 'female' ? t.common.female : (gender || '');
}

/** The params that hold a gender (GENDER_MISMATCH sends `redGender` and `blueGender`). */
const GENDER_PARAMS = new Set(['redGender', 'blueGender', 'gender']);

/** The params that hold a role id (`role`) or a comma list of role ids (`allowed`, which
 *  INVALID_ROLE and INVALID_REQUESTED_ROLE send). The two other codes that send `allowed`
 *  (INVALID_WINNER: red, blue, draw; AGE_GROUP_NOT_ALLOWED: age groups) hold no role name and
 *  no underscore, so `roleLabelOf` gives each item back as it is. */
function paramText(key: string, raw: string | number, t: Translations): string {
  if (key === 'role') return roleLabelOf(t, String(raw));
  if (GENDER_PARAMS.has(key)) return genderLabelOf(t, String(raw));
  if (key === 'allowed') return String(raw).split(',').map(item => roleLabelOf(t, item.trim())).join(', ');
  return String(raw);
}

/**
 * Fill each `{name}` of a template from `params`. A missing param gives an
 * empty string. The param named `role` is a role id and the param named
 * `allowed` is a comma list of role ids: each shows as the label of the
 * language of `t`. The params `redGender`, `blueGender` and `gender` hold the
 * English word of a gender and show the same way. Every other param shows as it is.
 */
export function fillTemplate(tpl: string, params: MessageParams | null | undefined, t: Translations): string {
  return tpl.replace(/\{(\w+)\}/g, (_m, key: string) => {
    const raw = params?.[key];
    if (raw === undefined || raw === null) return '';
    return paramText(key, raw, t);
  });
}
